/**
 * KAN-827 — the ONE source of company-lock wording. Every surface (dialog,
 * dashboard row, Plan & billing sub-line, in-form notice) reads it, so the
 * wording follows (level, reason, role) the same everywhere. Pure: returns
 * message descriptors `{ id, defaultMessage }`; the caller formats them.
 *
 *   admin      = owner + admins (role 'admin')
 *   office     = billing / operations (and any unknown role)
 *   field      = worker / supervisor
 *   level      = 'read_only' | 'suspended' (anything else -> null)
 *   reason     = 'trial_ended' | 'past_due' | 'cancelled' | 'dispute_lost' (unknown/null -> trial_ended)
 *                dispute_lost (KAN-1036): the bank disputed a payment; behaves like past_due (verb 'pay') with its own calm wording
 */
const m = (id, defaultMessage) => ({ id, defaultMessage });

const REASONS = ['trial_ended', 'past_due', 'cancelled', 'dispute_lost'];
export const normalizeLockReason = (reason) => (REASONS.includes(reason) ? reason : 'trial_ended');

export const isAdminRole = (role) => role === 'admin';
export const isFieldRole = (role) => role === 'worker' || role === 'supervisor';

/** The action the owner takes to lift the lock. */
export function lockVerb(level, reason) {
  const r = normalizeLockReason(reason);
  if (level === 'suspended') return r === 'trial_ended' ? 'subscribe' : 'pay';
  if (r === 'past_due' || r === 'dispute_lost') return 'pay';
  return r === 'cancelled' ? 'resubscribe' : 'subscribe';
}

const TITLE = {
  read_only: m('companyAccess.dialog.title', 'Account is read-only'),
  suspended: m('companyAccess.dialog.titleSuspended', 'Account is suspended'),
};
const READ_ONLY_BODY = {
  trial_ended: m('companyAccess.copy.readOnly.body.trial_ended', 'Your crew can still finish their routes. Some office work is paused until the company subscribes.'),
  past_due: m('companyAccess.copy.readOnly.body.past_due', 'Your crew can still finish their routes. Some office work is paused until the payment is made.'),
  cancelled: m('companyAccess.copy.readOnly.body.cancelled', 'Your crew can still finish their routes. Some office work is paused until the company resubscribes.'),
  dispute_lost: m('companyAccess.copy.readOnly.body.dispute_lost', 'Your crew can still finish their routes. Some office work is paused. Your bank disputed a CrewVex payment. Contact us to restore full access.'),
};
const SUSPENDED_DISPUTE_BODY = m('companyAccess.copy.suspended.body.dispute_lost', 'Nobody in your company can log visits or make changes. Your bank disputed a CrewVex payment. Contact us to restore full access. You can still view everything.');
const SUSPENDED_BODY = {
  subscribe: m('companyAccess.copy.suspended.body.subscribed', 'Nobody in your company can log visits or make changes until the account is subscribed. You can still view everything.'),
  pay: m('companyAccess.copy.suspended.body.paid', 'Nobody in your company can log visits or make changes until the account is paid. You can still view everything.'),
};
const FIELD_BODY = m('companyAccess.copy.suspended.body.field', "Your company's account is suspended, so visits can't be logged right now. Ask the owner.");
const ASK = {
  subscribe: m('companyAccess.copy.ask.subscribe', 'Ask the owner to subscribe.'),
  pay: m('companyAccess.copy.ask.pay', 'Ask the owner to pay.'),
  resubscribe: m('companyAccess.copy.ask.resubscribe', 'Ask the owner to resubscribe.'),
};
const OPEN_PLAN = m('companyAccess.dialog.openPlan', 'Open Plan & billing');

const IN_FORM_READ_ONLY = {
  trial_ended: m('companyAccess.copy.inForm.readOnly.trial_ended', "Changes can't be saved until the company subscribes."),
  past_due: m('companyAccess.copy.inForm.readOnly.past_due', "Changes can't be saved until the payment is made."),
  cancelled: m('companyAccess.copy.inForm.readOnly.cancelled', "Changes can't be saved until the company resubscribes."),
  dispute_lost: m('companyAccess.copy.inForm.readOnly.dispute_lost', "Changes can't be saved. Your bank disputed a CrewVex payment, so contact us."),
};
const IN_FORM_SUSPENDED = {
  subscribe: m('companyAccess.copy.inForm.suspended.subscribed', "Visits and changes can't be saved until the account is subscribed."),
  pay: m('companyAccess.copy.inForm.suspended.paid', "Visits and changes can't be saved until the account is paid."),
};
const IN_FORM_SUSPENDED_DISPUTE = m('companyAccess.copy.inForm.suspended.dispute_lost', "Visits and changes can't be saved. Your bank disputed a CrewVex payment, so contact us.");
const IN_FORM_FIELD = m('companyAccess.copy.inForm.suspended.field', "Visits can't be logged right now. Ask the owner.");

/**
 * @returns {null | { title, body, ask: object|null, primaryLabel: object|null,
 *   inFormLine: object, inFormAsk: object|null, verb: string }}
 *   `ask` is the dialog's trailing "Ask the owner…" (office roles only); the
 *   field line already contains its own ask.
 */
export function companyAccessCopy(level, reason, role) {
  if (level !== 'read_only' && level !== 'suspended') return null;
  const r = normalizeLockReason(reason);
  const verb = lockVerb(level, r);
  const admin = isAdminRole(role);
  const field = isFieldRole(role) && level === 'suspended';
  let body;
  let inFormLine;
  if (field) {
    body = FIELD_BODY;
    inFormLine = IN_FORM_FIELD;
  } else if (level === 'suspended') {
    body = r === 'dispute_lost' ? SUSPENDED_DISPUTE_BODY : SUSPENDED_BODY[verb];
    inFormLine = r === 'dispute_lost' ? IN_FORM_SUSPENDED_DISPUTE : IN_FORM_SUSPENDED[verb];
  } else {
    body = READ_ONLY_BODY[r];
    inFormLine = IN_FORM_READ_ONLY[r];
  }
  const ask = admin || field ? null : ASK[verb];
  return {
    title: TITLE[level],
    body,
    ask,
    primaryLabel: admin ? OPEN_PLAN : null,
    inFormLine,
    inFormAsk: ask,
    verb,
  };
}

const ROW_ADMIN = {
  subscribe: m('companyAccess.notice.sub.admin.subscribe', 'Open Plan & billing to subscribe'),
  pay: m('companyAccess.notice.sub.admin.pay', 'Open Plan & billing to pay'),
  resubscribe: m('companyAccess.notice.sub.admin.resubscribe', 'Open Plan & billing to resubscribe'),
};
const ROW_OFFICE = {
  subscribe: m('companyAccess.notice.sub.office.subscribe', 'Ask the owner to subscribe'),
  pay: m('companyAccess.notice.sub.office.pay', 'Ask the owner to pay'),
  resubscribe: m('companyAccess.notice.sub.office.resubscribe', 'Ask the owner to resubscribe'),
};
const ROW_FIELD = m('companyAccess.notice.sub.field', "Visits can't be logged. Ask the owner.");

/** Dashboard row sub-line (the title stays "Your account is read-only/suspended"). */
export function companyAccessRowSub(level, reason, role) {
  if (level !== 'read_only' && level !== 'suspended') return null;
  const verb = lockVerb(level, reason);
  if (isAdminRole(role)) return ROW_ADMIN[verb];
  if (isFieldRole(role)) return level === 'suspended' ? ROW_FIELD : null;
  return ROW_OFFICE[verb];
}

const PLAN_READ_ONLY = {
  trial_ended: m('company.plan.access.readOnlyUntil.trial_ended', 'Read-only until the company subscribes'),
  past_due: m('company.plan.access.readOnlyUntil.past_due', 'Read-only until the payment is made'),
  cancelled: m('company.plan.access.readOnlyUntil.cancelled', 'Read-only until the company resubscribes'),
  dispute_lost: m('company.plan.access.readOnlyUntil.dispute_lost', 'Read-only until the payment dispute is settled'),
};
const PLAN_SUSPENDED = {
  subscribe: m('company.plan.access.suspendedUntil.subscribed', 'Suspended until the account is subscribed'),
  pay: m('company.plan.access.suspendedUntil.paid', 'Suspended until the account is paid'),
};

/** Plan & billing card sub-line: adds the "until" the mark does not say. */
export function companyAccessPlanSub(level, reason) {
  if (level === 'read_only') return PLAN_READ_ONLY[normalizeLockReason(reason)];
  if (level === 'suspended') return PLAN_SUSPENDED[lockVerb(level, reason)];
  return null;
}

const PLAN_CTA = {
  subscribe: m('company.plan.billing.emailTo.subscribe', 'Email {email} to subscribe.'),
  pay: m('company.plan.billing.emailTo.pay', 'Email {email} to pay.'),
  resubscribe: m('company.plan.billing.emailTo.resubscribe', 'Email {email} to resubscribe.'),
};
const PLAN_CTA_TITLE = {
  subscribe: m('company.plan.billing.cta.subscribe', 'Subscribe'),
  pay: m('company.plan.billing.cta.pay', 'Pay'),
  resubscribe: m('company.plan.billing.cta.resubscribe', 'Resubscribe'),
};

/**
 * The Plan & billing "how to fix it" row (temporary until Stripe).
 * `level` is the access level; for a grace level the verb comes from the billing reason.
 */
export function companyAccessPlanCta(level, reason) {
  if (level !== 'grace' && level !== 'read_only' && level !== 'suspended') return null;
  const verb = level === 'grace' ? lockVerb('read_only', reason) : lockVerb(level, reason);
  return { title: PLAN_CTA_TITLE[verb], line: PLAN_CTA[verb] };
}

/**
 * KAN-204 Ship B — the full-screen "Your company's account is suspended" screen
 * a signed-in person lands on instead of the app. Same title for everyone; the
 * body follows (reason, role): the owner / admins are told to act, everyone
 * else is told to ask the owner. `verb` is subscribe (trial ended) or pay.
 */
const SCREEN_TITLE = m('companyAccess.screen.title', "Your company's account is suspended");
const SCREEN_BODY_OFFICE = {
  subscribe: m('companyAccess.screen.body.subscribe', 'Ask the owner to subscribe to start using CrewVex again.'),
  pay: m('companyAccess.screen.body.pay', 'Ask the owner to pay to start using CrewVex again.'),
};
const SCREEN_BODY_ADMIN = {
  subscribe: m('companyAccess.screen.bodyAdmin.subscribe', 'Subscribe to start using CrewVex again.'),
  pay: m('companyAccess.screen.bodyAdmin.pay', 'Pay to start using CrewVex again.'),
};

/** @returns {{ title: object, body: object, verb: 'subscribe'|'pay' }} message descriptors */
export function companyAccessSuspendedScreen(reason, role) {
  const verb = lockVerb('suspended', reason);
  return {
    title: SCREEN_TITLE,
    body: (isAdminRole(role) ? SCREEN_BODY_ADMIN : SCREEN_BODY_OFFICE)[verb],
    verb,
  };
}

/**
 * KAN-204 Ship C — the sign-in greeting card ("something changed that needs
 * you", once per state change per person). One source for wording + tone.
 *
 *   admin (owner + admins): grace (amber Hourglass) and read_only (red Lock); Later + Open Plan & billing
 *   billing / operations:   read_only only, a lighter neutral card, ONE "Got it" button
 *   anyone else / full / suspended (the Ship B screen covers it): null
 *
 * `nextStepOn` is a company-local YYYY-MM-DD. A missing / malformed date drops
 * the date sentence (the no-date body variants) — never a blank.
 */
const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The valid YYYY-MM-DD or null. */
export function normalizeNextStepOn(value) {
  const hit = typeof value === 'string' ? YMD.exec(value.trim()) : null;
  if (!hit) return null;
  const [y, mo, d] = [Number(hit[1]), Number(hit[2]), Number(hit[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  const ok = probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
  return ok ? `${hit[1]}-${hit[2]}-${hit[3]}` : null;
}

/**
 * "October 22" / "22 de octubre" — FULL month name, never abbreviated (an
 * abbreviated Spanish month rendered "oct.." in emails). The calendar date is
 * pinned to UTC noon and formatted in UTC, so no device zone can move the day
 * and no "today" is ever read. `locale` is 'en' | 'es' (anything else: en).
 */
export function formatGreetingDate(ymd, locale) {
  const date = normalizeNextStepOn(ymd);
  if (!date) return '';
  const [y, mo, d] = date.split('-').map(Number);
  const when = new Date(Date.UTC(y, mo - 1, d, 12));
  return new Intl.DateTimeFormat(String(locale || 'en').startsWith('es') ? 'es' : 'en', {
    month: 'long', day: 'numeric', timeZone: 'UTC',
  }).format(when);
}

const GREET_TITLE = {
  trial_ended: m('companyAccess.greeting.title.trial_ended', 'Your trial has ended'),
  past_due: m('companyAccess.greeting.title.past_due', 'Your payment is past due'),
  cancelled: m('companyAccess.greeting.title.cancelled', 'Your subscription is ending'),
};
const GREET_READ_ONLY_TITLE = m('companyAccess.greeting.title.readOnly', 'Your account is now read-only');
const GREET_BODY_DATED = {
  trial_ended: m('companyAccess.greeting.body.trial_ended', 'On {date}, your account becomes read-only until you subscribe. Your crew can keep working their routes.'),
  past_due: m('companyAccess.greeting.body.past_due', 'To keep full access, please pay before {date}. On that day, your account becomes read-only.'),
  cancelled: m('companyAccess.greeting.body.cancelled', 'On {date}, your account becomes read-only. You can resubscribe anytime from Plan & billing.'),
};
const GREET_BODY_UNDATED = {
  trial_ended: m('companyAccess.greeting.bodyNoDate.trial_ended', 'Your account will become read-only until you subscribe. Your crew can keep working their routes.'),
  past_due: m('companyAccess.greeting.bodyNoDate.past_due', 'To keep full access, please pay soon. Otherwise, your account becomes read-only.'),
  cancelled: m('companyAccess.greeting.bodyNoDate.cancelled', 'Your account will become read-only when your access ends. You can resubscribe anytime from Plan & billing.'),
};
const GREET_LATER = m('companyAccess.greeting.later', 'Later');
const GREET_GOT_IT = m('companyAccess.greeting.gotIt', 'Got it');

/**
 * @returns {null | { kind: 'admin'|'office', tone: 'amber'|'red'|'neutral', icon: 'hourglass'|'lock',
 *   title: object, body: object, needsDate: boolean, date: string|null, ask: object|null,
 *   dismissLabel: object|null, primaryLabel: object|null, reason: string }}
 *   message descriptors; the caller formats them (`{date}` = formatGreetingDate(date, locale)).
 */
export function companyAccessGreeting(level, reason, role, nextStepOn) {
  if (level !== 'grace' && level !== 'read_only') return null;
  const admin = role === 'admin';
  const office = role === 'billing' || role === 'operations';
  if (!admin && !office) return null;
  if (level === 'grace' && !admin) return null;
  const r = normalizeLockReason(reason);
  const date = normalizeNextStepOn(nextStepOn);
  if (level === 'grace') {
    return {
      kind: 'admin', tone: 'amber', icon: 'hourglass', reason: r, date, level, role,
      title: GREET_TITLE[r],
      body: (date ? GREET_BODY_DATED : GREET_BODY_UNDATED)[r],
      needsDate: Boolean(date), ask: null,
      dismissLabel: GREET_LATER, primaryLabel: OPEN_PLAN,
    };
  }
  return {
    kind: admin ? 'admin' : 'office', tone: admin ? 'red' : 'neutral', icon: 'lock', reason: r, date, level, role,
    title: GREET_READ_ONLY_TITLE,
    body: READ_ONLY_BODY[r],
    needsDate: false,
    ask: admin ? null : ASK[lockVerb('read_only', r)],
    dismissLabel: admin ? GREET_LATER : GREET_GOT_IT,
    primaryLabel: admin ? OPEN_PLAN : null,
  };
}
